'use client';

import { ThemeProvider as NextThemesProvider } from 'next-themes';

type Props = {
  children: React.ReactNode;
  defaultTheme: 'light' | 'dark';
};

// next-themes ignores a provider nested inside another one, so there is no root
// provider: each route group mounts exactly one of these in its layout, with its own
// default. Every group needs one, because the provider also ships the pre-paint
// script that applies the stored theme on a hard reload. The storage key is shared:
// the landing and the app live on different hosts, so their stored choices are
// already separate origins.
// The pre-paint script only has to run in the server HTML. When a provider mounts on
// a client navigation, React never executes the script and logs an error for it, so
// there it is typed as a data block, which React skips.
// Takes the environment as an argument so a test can reach the server arm, which
// jsdom never runs.
export const themeScriptType = (isServer: boolean) =>
  isServer ? 'text/javascript' : 'application/json';

const scriptProps = { type: themeScriptType(typeof window === 'undefined') };

export const ThemeProvider: React.FC<Props> = ({ children, defaultTheme }) => {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={defaultTheme}
      disableTransitionOnChange
      scriptProps={scriptProps}
    >
      {children}
    </NextThemesProvider>
  );
};
